import {
  COMMIT_FORM_FIELD_LIST,
  COMMIT_FORM_FIELDS_KEY,
} from '../store/supply-visibility/supply-visibility.consts';
import { toStoredColumns } from '../utils/general.utils';
import { loadDefaultColumns } from '../utils/columns.utils';
import { Column } from '@orion-platform/datagrid';
import { formatDate } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  AuthenticationService,
  ConfigService,
  QapConfig,
} from '@orion-platform/core';
import { environment } from '@orion/environment';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import {
  ChartSetting,
  DateRangeParameters,
  DummyCommitHeader,
  ExcelDownloadInstructions,
  LoadState,
  NewUserSetting,
  PartNoVendorUrl,
  PNVendor,
  PnVendorCode,
  PrecalculationResponse,
  StaticValue,
  SvUrlWithParams,
  TemplatePayload,
  UserSettings,
  WaterfallDto,
} from 'apps/orion/src/app/models/supply-visibility.model';
import { NotificationService } from 'libs/core/src/lib/services';
import { firstValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
  CommonCommitFields,
  DialogDetail,
  DummyCommitHeadersFilter,
  RequestStatus,
} from '../models/commit.model';
import { Forecast, ForecastDetail, ForecastVirtualVc } from '../models/forecast.model';
import { MaterialManagementViews } from '../models/new-mm-view.model';
import { DashboardSetting, SharedWidget, SharedWidgets } from '../models/shared-sv.model';
import {
  ApiResponse,
  BasicParameters,
  Commit,
  CumulativeListDownloadPayload,
  CumulativeListDownloadResponse,
  CumulativePayload,
  DummyCommitDetail,
  EnumCommitFieldStructure,
  ForecastDate,
  ManufacturerDetailsDto,
  NewCommit,
  PnReviewed,
  PnVcFlags,
  PurchaseOrders,
  ValidCombination,
} from '../models/supply-visibility.model';
import { AdditionalFilter, DashboardPanelModel, DashboardType } from '../models/sv-dashboard';
import { SVNotification } from '../models/sv-notifications.model';
import { PartNumberVendorCodes } from '../models/vendor-codes-rights.model';
import { VirtualPNDetail } from '../models/virtual-pn-groups.model';
import { reconcileColumns } from '../utils/columns.utils';

import {
  DeletePnFlags,
  SetDateRangeParameters,
  SetPnReviewed,
} from '../store/supply-visibility/supply-visibility.actions';
import { COMMITS_COLUMN_LIST } from '../store/supply-visibility/supply-visibility.consts';
import { DummyCommitReturnHeader } from '../store/supply-visibility/supply-visibility.types';
import { orderDefaults } from '../store/supply-visibility/supply-visibility.utils';
import { FileUploadService } from './file-upload.service';
import { VirtualPnGroupsService } from './virtual-pn-groups.service';
import { ColumnsState } from '../types/columns.types';

@Injectable({
  providedIn: 'root',
})
export class SupplyVisibilityService {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);
  private readonly authService = inject(AuthenticationService);
  private readonly vpnsService = inject(VirtualPnGroupsService);
  private readonly fileUpload = inject(FileUploadService);
  private readonly notificationService = inject(NotificationService);

  private store = inject(Store);

  private userSettingsUrl: unknown;
  private apiUrl!: string;
  public svUrlList: SvUrlWithParams[] = [];
  private dummyCommitHeader!: DummyCommitHeader;
  isChildList: boolean = false;
  private currentParentPNs!: BasicParameters[];
  currentChildPNs: unknown;
  private svCalled: boolean = false;
  private isColumnLoaded = new Subject<boolean>();
  private loadingDetails = new Subject<LoadState>();
  private dialogClose = new Subject<DialogDetail>();
  private chartSettings = new Subject<unknown>();
  private expandedSettings = new Subject<unknown>();
  private isFilterApplied = new Subject<boolean>();

  isLoaded: LoadState = {
    isCommentLoaded: null,
    isCommitsLoaded: false,
    isForecastLoaded: false,
    isInfoLoaded: null,
    isNotesLoaded: null,
  };

  constructor() {
    this.apiUrl = this.configService.getSettings('apiBaseUrl') as string;
    this.userSettingsUrl = this.configService.getSettings('userSettingsService');
  }

  get dummyCommitHeaderForExcel(): DummyCommitHeader {
    return this.dummyCommitHeader;
  }

  set dummyCommitHeaderForExcel(dummyCommitHeader: DummyCommitHeader) {
    this.dummyCommitHeader = dummyCommitHeader;
  }

  getDefaultColumns = (key: string, sortByStored: boolean): Observable<ColumnsState> => {
    return this.getDefaultFormSettingFields(key).pipe(
      switchMap((formFields: string[]): Observable<ColumnsState> => {
        const defaultFields = new CommonCommitFields(
          EnumCommitFieldStructure.CommitTable,
          formFields,
        ).fields;
        const defaultSorted = orderDefaults(formFields, defaultFields);
        const fallback = {
          columns: defaultSorted,
          defaultColumns: true,
          defaultFields: formFields,
        };

        if (!sortByStored) {
          // Same logic as error: use fallback (default column sorting).
          return of(fallback);
        }

        // Try stored settings; on error (or no data), use the same fallback with default column sorting.
        return this.getUserSettings(COMMITS_COLUMN_LIST).pipe(
          map((settings: UserSettings): ColumnsState => {
            const hasStored = Boolean(settings.data);
            return hasStored
              ? {
                  columns: reconcileColumns(defaultFields, JSON.parse(settings.data)),
                  defaultColumns: false,
                  defaultFields: formFields,
                }
              : fallback;
          }),
          // Default column sorting. On 404 persist defaults so the next load hits 200.
          catchError((error): Observable<ColumnsState> => {
            if (error?.status === 404) {
              this.addUserSettings(
                { data: JSON.stringify(fallback.columns), key: COMMITS_COLUMN_LIST },
                false,
              ).subscribe();
            }
            return of(fallback);
          }),
        );
      }),
    );
  };

  computeIsChild(
    isChildList?: boolean | null,
    partNumber?: { vendorCode?: string },
    paramsForChart?: Params,
  ): boolean | null {
    if (typeof isChildList === 'boolean') {
      this.isChildList = isChildList;
    }

    const isVirtual = partNumber?.vendorCode === 'VirtualVC';
    if (isVirtual || !this.isChildList) {
      this.isChildList = false;
      return null;
    }

    return Boolean(partNumber || paramsForChart?.['child']);
  }

  getDialogDetail(): Observable<DialogDetail> {
    return this.dialogClose.asObservable();
  }

  setDialogDetail(data?: DialogDetail): void {
    this.dialogClose.next(data as DialogDetail);
  }

  getChartSetting(): Observable<unknown> {
    return this.chartSettings.asObservable();
  }

  setChartSetting(data?: unknown): void {
    this.chartSettings.next(data);
  }

  getChartSettingsExpanded(): Observable<unknown> {
    return this.expandedSettings.asObservable();
  }

  setChartSettingsExpanded(expanded?: boolean): void {
    this.expandedSettings.next(expanded);
  }

  getLoadingDetail(): Observable<LoadState> {
    return this.loadingDetails.asObservable();
  }

  setLoadingDetail(data: LoadState): void {
    this.isLoaded = { ...this.isLoaded, ...data };
    this.loadingDetails.next(this.isLoaded);
  }

  setParentPN(data: BasicParameters[]): void {
    let tData = Helper.createCopy(data);
    this.currentParentPNs = tData;
  }

  getIsFilterApplied(): Observable<boolean> {
    return this.isFilterApplied.asObservable();
  }

  getParentPN(): BasicParameters[] {
    let tData = Helper.createCopy(this.currentParentPNs);
    return tData;
  }

  resetValues(): void {
    this.isLoaded = {
      isCommentLoaded: null,
      isCommitsLoaded: false,
      isForecastLoaded: false,
      isInfoLoaded: null,
      isNotesLoaded: null,
    };
  }

  /**
   * Get API URL for UserSettings
   * @param {string} action e.g. AddUserSettings, UpdateUserSettings, DeleteUserSettings,GetUserSettings
   * @returns
   * @memberof SupplyVisibilityService
   * (GET)MaterialManagement/UserSettings/GetUserSettings?key=materialDashboard
   */
  getApiUrlUserSettings(action: string): string {
    return `${this.userSettingsUrl}${environment.modulesBaseUrl.materialManagement.userSettings}/${action}`;
  }

  /**
   * Get API URL for Commits
   * @param {string} action
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrlCommits(action: string): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.commits}/${action}`;
  }

  /**
   * Get API URL for PurchaseOrders
   * @param {string} action
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrlPurchaseOrders(action: string): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.purchaseOrders}/${action}`;
  }

  /**
   * Get API URL for DummyCommits
   * @param {string} action
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrlDummyCommits(action: string): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.dummyCommits}/${action}`;
  }

  /**
   * Get API URL for PartNumbers
   * @param {string} action
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrlPartNumbers(action: string): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.partNumbers}/${action}`;
  }

  /**
   * Get manufacturer details for pn and vc.
   * @returns
   * @memberof SupplyVisibilityService
   */
  getManufacturerDetails(
    partNumber: string,
    vendorCode: string,
  ): Observable<ManufacturerDetailsDto[]> {
    let url = this.getApiUrlPartNumbers('getpartnumberciscodatas');
    let params = new HttpParams().set('partNumber', partNumber).set('vendorCode', vendorCode);
    return this.http.get<ManufacturerDetailsDto[]>(url, { params: params });
  }

  getHeaderForPrecalculate(): Observable<DummyCommitReturnHeader> {
    return this.http.get<DummyCommitReturnHeader>(
      `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.getHeaderForPrecalculate}`,
    );
  }

  /**
   * Get API URL for Vendor codes
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrlWaterfall(): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.waterfall}`;
  }

  /**
   * Get API URL for Supply Visibility
   * @param {string} action
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrlSupplyVisibility(action: string): string {
    return `${this.apiUrl}${
      environment.modulesBaseUrl.materialManagement.supplyVisibility
    }${'/'}${action}`;
  }

  /**
   * Get comits by date range
   * @param {string} key keyName of UserSetting
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (GET)MaterialManagement/UserSettings/GetUserSettings?key=materialDashboard
   */
  getUserSettings(key: string): Observable<UserSettings> {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
    return this.http.get<UserSettings>(`${this.getApiUrlUserSettings('')}${key}-${plant}`, {});
  }

  /**
   * Add new UserSetting
   * @param {NewUserSetting} userSetting
   * @param updateIdInData If true add Plant ID (eg. -LS51) to ID of object in data field
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (PUT)MaterialManagement/UserSettings/AddUserSettings - change according api
   */
  addUserSettings(
    userSetting: NewUserSetting,
    updateIdInData: boolean = true,
  ): Observable<unknown> {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
    userSetting.key += '-' + plant;
    if (userSetting.data) {
      let data = JSON.parse(userSetting.data);
      if (updateIdInData === true) {
        data.id += '-' + plant;
      }
      userSetting.data = JSON.stringify(data);
    }
    return this.http.post(`${this.getApiUrlUserSettings('')}`, userSetting);
  }

  /**
   * Update existing userSetting
   * @param {NewUserSetting} userSetting
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (POST)MaterialManagement/UserSettings/UpdateUserSettings
   */
  updateUserSettings(userSetting: NewUserSetting): Observable<unknown> {
    const key = userSetting.key;
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
    userSetting.key += '-' + plant;
    return this.http.put(`${this.getApiUrlUserSettings('')}${key}-${plant}`, userSetting);
  }

  /**
   * Delete existing userSetting
   * @param {string} key
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (DELETE)MaterialManagement/UserSettings/DeleteUserSettings
   */
  deleteUserSettings(key: string): Observable<unknown> {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
    return this.http.delete(`${this.getApiUrlUserSettings('')}${key}-${plant}`, {});
  }

  /**
   * Get comits by date range
   * @param {string} dashboard keyName of dashboard
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (GET)MaterialManagement/UserSettings/GetUserSettings?key=materialDashboard
   */
  /**
   * Add new UserSetting
   * @param {NewUserSetting} userSetting
   * @param updateIdInData If true add Plant ID (eg. -LS51) to ID of object in data field
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (PUT)MaterialManagement/UserSettings/AddUserSettings - change according api
   */
  addCommonUserSettings(userSetting: NewUserSetting): Observable<UserSettings> {
    if (userSetting.data) {
      let data = JSON.parse(userSetting.data);
      userSetting.data = JSON.stringify(data);
    }
    return <Observable<UserSettings>>(
      this.http.put(`${this.getApiUrlUserSettings('')}`, userSetting)
    );
  }

  /**
   * Update existing userSetting
   * @param {NewUserSetting} userSetting
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (POST)MaterialManagement/UserSettings/UpdateUserSettings
   */
  updateCommonUserSettings(userSetting: NewUserSetting): Observable<UserSettings> {
    const key = userSetting.key;
    return <Observable<UserSettings>>(
      this.http.put(`${this.getApiUrlUserSettings('')}${key}`, userSetting)
    );
  }

  deleteCommonUserSettings(key: string): Observable<unknown> {
    const url = `${this.getApiUrlUserSettings('')}${key}`;
    return this.http.delete(url, {});
  }

  //Get default commits columns and fields
  getDefaultFormSettingFields(formKey: string): Observable<string[]> {
    let url = new URL(
      `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.getDefaultFormSettingFields}`,
    );
    url.searchParams.append('formKey', formKey);

    console.log(url.toString());
    return this.http.get<string[]>(url.toString());
  }

  SVDefaultDashboard: DashboardPanelModel[] = [
    {
      cols: 3,
      firstValue: { onlyMyPNs: true, weeks: 26 },
      header: '',
      id: 'simplePNList-' + uuidv4(),
      link: '',
      minItemCols: 3,
      minItemRows: 5,
      modificationRights: true,
      rows: 5,
      title: 'All My Part Numbers',
      type: 'simplePNList',
      x: 0,
      y: 0,
    },
    {
      cols: 3,
      id: 'reviewSupplyVisibility-' + uuidv4(),
      minItemCols: 3,
      minItemRows: 4,
      modificationRights: true,
      rows: 4,
      title: 'Search',
      type: 'reviewSupplyVisibility',
      x: 3,
      y: 0,
    },
    {
      cols: 2,
      id: 'configurationLink-' + uuidv4(),
      link: 'material-management/material-management-views',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 2,
      modificationRights: true,
      rows: 2,
      title: 'View config',
      type: 'configurationLink',
      x: 6,
      y: 0,
    },
    {
      cols: 2,
      id: 'commitsLink-' + uuidv4(),
      link: 'material-management/commits',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 2,
      modificationRights: true,
      rows: 2,
      title: 'Commits',
      type: 'commitsLink',
      x: 8,
      y: 0,
    },
    {
      cols: 2,
      id: 'pnGroupsLink-' + uuidv4(),
      link: 'material-management/pn-groups',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 2,
      modificationRights: true,
      rows: 2,
      title: 'Pn Groups',
      type: 'pnGroupsLink',
      x: 10,
      y: 0,
    },
    {
      cols: 2,
      id: 'vendorcodeRightsLink-' + uuidv4(),
      link: 'material-management/vendorcodes-rights',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 2,
      modificationRights: true,
      rows: 2,
      title: 'Vendor Code Rights',
      type: 'vendorcodeRightsLink',
      x: 6,
      y: 2,
    },
    {
      cols: 2,
      id: 'qapLink-' + uuidv4(),
      link: 'material-management/qap',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 2,
      modificationRights: true,
      rows: 2,
      title: 'QAP View',
      type: 'qapLink',
      x: 6,
      y: 2,
    },
    {
      cols: 2,
      id: 'aiLink-' + uuidv4(),
      link: 'dashboard/ai',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 2,
      modificationRights: true,
      rows: 2,
      title: 'A.I.',
      type: 'aiLink',
      x: 6,
      y: 2,
    },
  ];

  appendUuidForImportWidget(importData: DashboardSetting): unknown {
    const parsedData = JSON.parse(importData.data as string);
    parsedData.forEach((item: DashboardPanelModel): void => {
      if (item.id) {
        item.id = item.id + '-' + uuidv4();
      }
    });
    importData.data = JSON.stringify(parsedData);
    return importData.data;
  }

  DEFAULT_dashboardTypeList: DashboardType[] = [
    {
      cols: 3,
      fontSize: 'medium',
      img: 'review-supply-visibility',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 4,
      modificationRights: true,
      rows: 4,
      selected: false,
      title: 'Search',
      type: 'reviewSupplyVisibility',
    },
    {
      cols: 3,
      firstValue: { onlyMyPNs: true, weeks: 26 },
      fontSize: 'medium',
      img: 'smart-pn-list',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 5,
      modificationRights: true,
      rows: 5,
      selected: false,
      title: 'Smart PN list',
      type: 'smartPNList',
    },
    {
      cols: 4,
      firstValue: { onlyMyPNs: true },
      fontSize: 'medium',
      img: 'filter-icon',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 3,
      modificationRights: true,
      rows: 4,
      selected: false,
      title: 'Filter Widget',
      type: 'filterWidget',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'pn-groups-link',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'Pn Groups',
      type: 'pnGroupsLink',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'configuration-link',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'MM VIEWS',
      type: 'configurationLink',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'commits',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'Commits',
      type: 'commitsLink',
    },
    {
      cols: 2,
      firstValue: { onlyMyPNs: true, weeks: 26 },
      fontSize: 'medium',
      img: 'circular-chart',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      secondValue: { onlyMyPNs: true, weeks: 26 },
      selected: false,
      title: 'Circular Chart',
      type: 'circularChart',
    },
    {
      cols: 3,
      firstValue: { onlyMyPNs: true },
      fontSize: 'medium',
      img: 'search-v2',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 6,
      modificationRights: true,
      rows: 6,
      selected: false,
      title: 'Search 2.0',
      type: 'searchV2',
    },
    {
      chartColor: 'green',
      cols: 3,
      fontSize: 'medium',
      img: 'status-monitor',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 4,
      modificationRights: true,
      rows: 4,
      selected: false,
      selectMode: 'precalculations',
      title: 'Status Monitor',
      type: 'statusMonitor',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'vendorcode-rights-link',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'Vendor Code Rights',
      type: 'vendorcodeRightsLink',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'qap',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'Qap Views',
      type: 'qapLink',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'ai',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'A.I.',
      type: 'aiLink',
    },
    {
      cols: 3,
      firstValue: { filters: [] },
      fontSize: 'medium',
      img: 'commit-list',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 5,
      modificationRights: true,
      rows: 5,
      selected: false,
      title: 'Commit List',
      type: 'commitList',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'plo-sync',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'PLO Sync',
      type: 'ploSync',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'file-upload',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'File Upload',
      type: 'fileUpload',
    },
    {
      cols: 4,
      fontSize: 'medium',
      img: 'waterfall',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 4,
      minItemRows: 5,
      modificationRights: true,
      rows: 5,
      selected: false,
      title: 'Waterfall',
      type: 'waterfall',
    },
    {
      cols: 4,
      firstValue: { onlyMyPNs: true, showVendorCode: true, weeks: 26 },
      fontSize: 'medium',
      img: 'smart-pn-list',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 5,
      modificationRights: true,
      rows: 7,
      selected: false,
      subTitle: 'All My partnumbers',
      title: 'My Partnumbers',
      type: 'mypnlist',
    },
    {
      cols: 3,
      firstValue: { filters: [] },
      fontSize: 'medium',
      img: 'invoice-list',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 5,
      modificationRights: true,
      rows: 5,
      selected: false,
      title: 'Invoice List',
      type: 'invoiceList',
    },
    {
      cols: 2,
      fontSize: 'medium',
      img: 'portfolio',
      maxItemCols: 3,
      maxItemRows: 3,
      minItemCols: 2,
      minItemRows: 1,
      modificationRights: true,
      rows: 2,
      selected: false,
      title: 'Portfolio',
      type: 'portfolioLink',
    },
    {
      cols: 3,
      firstValue: { filters: [] },
      fontSize: 'medium',
      icon: 'local_shipping',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 5,
      model: 'list',
      modificationRights: true,
      rows: 5,
      selected: false,
      title: 'Shipments List',
      type: 'shipmentsList',
    },
    {
      cols: 3,
      firstValue: { filters: [] },
      fontSize: 'medium',
      icon: 'event',
      maxItemCols: null,
      maxItemRows: null,
      minItemCols: 3,
      minItemRows: 5,
      model: 'list',
      modificationRights: true,
      rows: 5,
      selected: false,
      title: 'Slot List',
      type: 'slotList',
    },
  ];

  getDashboardTypeList(widgetList: string[]): DashboardType[] {
    this.DEFAULT_dashboardTypeList.map((c): void => {
      c.selected = false;
    });
    this.DEFAULT_dashboardTypeList = Helper.getWidgetAccess(
      widgetList,
      this.DEFAULT_dashboardTypeList,
    );
    return this.DEFAULT_dashboardTypeList;
  }

  getDefaultDashboard(): DashboardPanelModel[] {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
    const defaultDashboard = this.SVDefaultDashboard;

    return defaultDashboard.map((panel): DashboardPanelModel => ({
      ...panel,
      id: panel.id ? `${panel.id}-${plant}` : panel.id,
    }));
  }

  /**
   * Get vendor codes list
   * @param {string} partNumbers list of part numbers
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (GET)MaterialManagement/UserSettings/GetUserSettings?key=materialDashboard
   */
  /**
   * Get vendor codes with names list
   * @param {string[]} partNumbers list of part numbers
   * @returns {Observable<PartNumberVendorCodes[]>}
   */
  getVendorCodeNamesList(partNumbers: string[]): Observable<PartNumberVendorCodes[]> {
    const tPartNumbers = partNumbers
      .map((str): string => (str ? str.toUpperCase().trim() : ''))
      .filter(Boolean);
    return this.http.post<PartNumberVendorCodes[]>(
      `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.getVendorCodeNames}`,
      tPartNumbers,
    );
  }

  getWaterfall(payload: WaterfallDto): Observable<Blob> {
    return this.http.post(`${this.getApiUrlWaterfall()}`, payload, {
      responseType: 'blob',
    });
  }

  /**
   * Get comits by date range
   * @param {BasicParameters} parameters
   * @param {DateRangeParameters} dateParams
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  getCommits(parameters: BasicParameters, dateParams: DateRangeParameters): Observable<Commit[]> {
    let params = new HttpParams();
    params = params.append('Plant', parameters.plant as string);
    params = params.append('PartNumber', parameters.partNumber as string);
    params = params.append('DateFrom', dateParams.dateFrom as string);
    params = params.append('DateTo', dateParams.dateTo as string);

    if (parameters.vendorCode !== null || parameters.vendorCode) {
      params = params.append('VendorCode', parameters.vendorCode as string);
    }

    return this.http.get<Commit[]>(`${this.getApiUrlCommits('GetCommits')}`, {
      params: params,
    });
  }

  /**
   * Get commits and dummy commits by date range
   * @param {BasicParameters} parameters
   * @param {DateRangeParameters} dateParams
   * @param {DummyCommitHeader} dummyCommitHeader
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  getCommitsWithDummy(
    parameters: BasicParameters,
    dateParams: DateRangeParameters,
    dummyCommitHeader: DummyCommitHeader,
    commitStatus?: string,
  ): Observable<Commit[]> {
    let params = new HttpParams();
    const activePlant = this.store.selectSnapshot(AuthenticationSelectors.activePlant);
    params = params.append('Plant', activePlant);
    params = params.append('PartNumber', (parameters.partNumber as string).toUpperCase());
    params = params.append('SapDeliveryDateFrom', dateParams.dateFrom as string);
    params = params.append('SapDeliveryDateTo', dateParams.dateTo as string);

    if (commitStatus != undefined && commitStatus.length > 0) {
      params = params.append('Status', commitStatus);
    }
    if (parameters.vendorCode !== null || parameters.vendorCode) {
      params = params.append('VendorCode', parameters.vendorCode as string);
    }
    if (dummyCommitHeader && dummyCommitHeader.id) {
      params = params.append('DummyCommitHeaderID', dummyCommitHeader.id);
    }

    return this.http.get<Commit[]>(`${this.getApiUrlCommits('GetCommitsWithDummy')}`, {
      params: params,
    });
  }

  /**
   * Add new Commit
   * @param {NewCommit} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  addCommit(commit: NewCommit): Observable<Commit> {
    commit = Helper.formatCommitDate(commit);
    return <Observable<Commit>>this.http.post(`${this.getApiUrlCommits('NewCommit')}`, commit);
  }

  /**
   * Update existing commit
   * @param {NewCommit} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  updateCommit(commit: NewCommit): Observable<Commit> {
    commit = Helper.formatCommitDate(commit);
    return <Observable<Commit>>this.http.post(`${this.getApiUrlCommits('EditCommit')}`, commit);
  }

  /**
   * Cancel commit
   * @param {CancelCommit} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  cancelCommit(commit: Commit): Observable<unknown> {
    return this.http.put(
      `${this.getApiUrlCommits('CommitCancellation')}/${commit.inboundDeliveryNumber}`,
      null,
    );
  }

  /**
   * Delete PLO
   * @param {DeletePLO} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  deletePLO(commit: Commit): Observable<unknown> {
    commit = Helper.formatCommitDate(commit);
    return this.http.post(`${this.getApiUrlCommits('DeleteCommit')}`, commit);
  }

  /**
   * Delete existing commit
   * @param {NewCommit} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  deleteCommit(commit: NewCommit): Observable<unknown> {
    commit = Helper.formatCommitDate(commit);
    return this.http.post(`${this.getApiUrlCommits('DeleteCommit')}`, commit);
  }

  /**
   * Update Dummy Commit
   * @param {DummyCommitDetail} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  addDummyCommit(commit: DummyCommitDetail): Observable<DummyCommitDetail> {
    commit = Helper.formatCommitDate(commit);
    return <Observable<DummyCommitDetail>>(
      this.http.post(`${this.getApiUrlDummyCommits('CreateDetail')}`, commit)
    );
  }

  /**
   * Update existing dummy commit
   * @param {DummyCommitDetail} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  updateDummyCommit(commit: DummyCommitDetail): Observable<Commit> {
    commit = Helper.formatCommitDate(commit);
    return <Observable<Commit>>(
      this.http.post(`${this.getApiUrlDummyCommits('UpdateDetail')}`, commit)
    );
  }

  /**
   * Delete existing dummy commit
   * @param {DummyCommitDetail} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (POST)MaterialManagement/SupplyVisibility/DeleteDummyCommitDetail
   */
  deleteDummyCommit(commit: DummyCommitDetail): Observable<unknown> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');

    const ret = this.http.post(
      `${this.getApiUrlDummyCommits('DeleteDetail')}?dummyCommitDetailId=${commit.id}`,
      {},
      { headers, responseType: 'text' },
    );
    return ret;
  }

  uploadDummyCommits(fileName: string, file: File): Observable<unknown> {
    const url = `${this.getApiUrlDummyCommits('PostUpload')}`;
    return this.fileUpload.uploadFormData(url, fileName, file);
  }

  batchEditSVRequestStatus(requestHash: string): Observable<RequestStatus> {
    return <Observable<RequestStatus>>(
      this.http.get(`${this.getApiUrlDummyCommits('-/RequestStatus/' + requestHash)}`)
    );
  }

  //DOWNLOAD//

  downloadDummyCommits(dummyCommitHeader: DummyCommitHeader): Observable<Blob> {
    return this.http.post(
      `${this.getApiUrlDummyCommits('GetUpload')}?dummyCommitHeaderId=${dummyCommitHeader.id}`,
      null,
      { responseType: 'blob' },
    );
  }

  /**
   * Add new Dummy Commit header
   * @param {DummyCommitHeader} commit header
   * @returns {Observable<any>}
   * @memberof SupplyVisibilityService
   */
  addDummyCommitHeader(commit: DummyCommitHeader): Observable<unknown> {
    return this.http.post(`${this.getApiUrlDummyCommits('CreateHeader')}`, commit);
  }

  /**
   * Update existing dummy commit header
   * @param {DummyCommitHeader} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   */
  updateDummyCommitHeader(commit: DummyCommitHeader): Observable<DummyCommitHeader> {
    return <Observable<DummyCommitHeader>>(
      this.http.post(`${this.getApiUrlDummyCommits('UpdateHeader')}`, commit)
    );
  }

  /**
   * Delete existing dummy commit header
   * @param {DummyCommitHeader} commit
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (POST)MaterialManagement/SupplyVisibility/DeleteDummyCommitHeader
   */
  deleteDummyCommitHeader(commit: DummyCommitHeader): Observable<unknown> {
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');

    const ret = this.http.post(
      `${this.getApiUrlDummyCommits('DeleteHeader')}?dummyCommitHeaderId=${commit.id}`,
      {},
      { headers, responseType: 'text' },
    );
    return ret;
  }

  /**
   * Update existing dummy commit header
   * @returns {Observable<any>}
   * @memberof SupplyVisibilityService
   */
  getDummyCommitHeaders(filter: DummyCommitHeadersFilter): Observable<DummyCommitHeader[]> {
    let params = new HttpParams();

    if (filter !== undefined && filter.vendorCode) {
      params = params.append('VendorCode', filter.vendorCode);
    }
    if (filter !== undefined && filter.partNumber) {
      params = params.append('partNumber', filter.partNumber.toUpperCase());
    }

    return <Observable<DummyCommitHeader[]>>this.http.get(
      `${this.getApiUrlDummyCommits('GetHeaders')}`,
      {
        params: params,
      },
    );
  }

  /**
   * Set PN as reviewed
   * @param {PnReviewed} pnReviewed
   * @returns {Observable<unknown>}
   * @memberof SupplyVisibilityService
   * (POST)MaterialManagement/SupplyVisibility/UpdatePrecalculateProjectionReviewed
   */
  setPnReviewed(pnReviewed: PnReviewed): Observable<PnVcFlags> {
    return <Observable<PnVcFlags>>(
      this.http.post(`${this.getApiUrlPartNumbers('createpartnumbervendorcodeflag')}`, pnReviewed)
    );
  }

  getPnFlags(pnVcList: PnVendorCode[]): Observable<PnVcFlags[]> {
    return this.http.post<PnVcFlags[]>(
      `${this.getApiUrlPartNumbers('getpartnumbervendorcodeflags')}`,
      pnVcList,
    );
  }

  deletePnFlags(pnVc: PnVendorCode, flag: unknown): Observable<unknown> {
    let partNumber, vendorCode;
    partNumber = encodeURIComponent(pnVc?.partNumber as string);
    vendorCode = encodeURIComponent(pnVc?.vendorCode as string);

    return this.http.delete(
      `${this.getApiUrlPartNumbers(
        'deletepartnumbervendorcodeflag',
      )}/${partNumber}/${vendorCode}/${flag}`,
    );
  }

  getMultiProjections(payload: CumulativePayload): Observable<Forecast[]> {
    return <Observable<Forecast[]>>(
      this.http.post(`${this.getApiUrlSupplyVisibility('MultiProjections')}`, payload)
    );
  }

  /**
   * Get Forecast data
   * @returns {Observable<any>}
   * @memberof SupplyVisibilityService
   */
  getForecast(
    parameters: BasicParameters,
    dummyCommitHeader?: DummyCommitHeader,
    requestFilter: boolean = false,
  ): Observable<Forecast> {
    if (parameters.vendorCode === null || !parameters.vendorCode) {
      return throwError((): Error => new Error('Invalid vendor code'));
    } else {
      this.svCalled = true;
    }

    const forecastUrlWithParams = this.getUrlForForecastWithParams(
      parameters,
      dummyCommitHeader,
      requestFilter,
    );
    if (forecastUrlWithParams === null) {
      // Handle the null case, return an observable with an error or take appropriate action.
      return throwError((): Error => new Error('Invalid forecastUrlWithParams'));
    }

    //this.svUrlList.push(forecastUrlWithParams);

    return <Observable<Forecast>>this.http.get(forecastUrlWithParams.url, {
      params: forecastUrlWithParams.params,
    });
  }

  getUrlForForecastWithParams(
    parameters: BasicParameters,
    dummyCommitHeader?: DummyCommitHeader,
    requestFilter: boolean = false,
  ): SvUrlWithParams {
    const selectedApi = 'ProjectionByVendor';
    let params = new HttpParams();
    params = params.append('Plant', parameters.plant as string);
    params = params.append(
      'PartNumber',
      encodeURIComponent(parameters.partNumber as string).toUpperCase(),
    );
    params = params.append('Records', (parameters.records as number).toString());
    params = params.append('Variant', parameters.variant as string);
    params = params.append('MaterialManagementViewID', parameters.mmViewID as string);
    params = params.append('VendorCode', parameters.vendorCode as string);
    if (dummyCommitHeader) {
      params = params.append('DummyCommitHeaderID', dummyCommitHeader.id as string);
    }
    if (requestFilter && parameters.vendorCode !== 'VirtualVC')
      params = params.append('requestFilter', 'true');

    const apiUrl = `${this.getApiUrlSupplyVisibility(selectedApi)}`;
    return {
      params: params,
      url: apiUrl.replace('__plant__', parameters.plant.toLowerCase()),
    };
  }

  /**
   * Get Forecast data for all vendors
   * @returns {Observable<any>}
   * @memberof SupplyVisibilityService
   */
  getForecastAllVendors(
    parameters: BasicParameters,
    dummyCommitHeader: DummyCommitHeader,
    vendorList: Array<{ partNumber: string; vendorCode: string }>,
  ): Observable<unknown> {
    return this.http.post(`${this.getApiUrlSupplyVisibility('MultiProjection')}`, {
      DummyCommitHeaderID: dummyCommitHeader !== null ? dummyCommitHeader.id : null,
      MaterialManagementViewID: parameters.mmViewID,
      partNoVendors: vendorList,
      PartNumber: parameters.partNumber,
      Plant: parameters.plant,
      records: parameters.records,
      variant: parameters.variant,
    });
  }

  /**
   * Get Children PN forecast data
   * @returns {ForecastVirtualVc[]}
   * @memberof SupplyVisibilityService
   */
  async getChildrenPnForecast(
    virtualPN: BasicParameters,
    virtualVcChildList: Forecast[],
  ): Promise<ForecastVirtualVc[]> {
    let virtualVcForecasts: ForecastVirtualVc[] = [];
    if (virtualVcChildList && virtualVcChildList.length) {
      for (let virtualVcChild of virtualVcChildList) {
        let rawValidQuote: Record<string, unknown> = await firstValueFrom(
          this.getValidCombination(virtualVcChild.partNumber, virtualVcChild.vendorCode),
        );
        const validQuota =
          rawValidQuote && rawValidQuote?.['isValidQuota']
            ? rawValidQuote?.['isValidQuota']
            : false;
        const basicParameters: BasicParameters = {
          mmViewID: virtualPN.mmViewID,
          partNumber: virtualVcChild.partNumber,
          plant: virtualPN.plant,
          records: virtualPN.records,
          variant: virtualPN.variant,
          vendorCode: virtualVcChild.vendorCode,
          widgetId: virtualPN.widgetId,
        };
        if (virtualVcChild && virtualVcChild.projectionDetails.length > 0) {
          if (virtualVcChild.staticvalues) {
            virtualVcChild.staticvalues = [
              {
                calculation: 'UNDEFINED',
                calculationrule: 'DEFAULT',
                calculationvalue: '0',
                label: 'Part Number',
                order: 0,
                value: virtualVcChild.partNumber,
                visible: true,
              },
              {
                calculation: 'UNDEFINED',
                calculationrule: 'DEFAULT',
                calculationvalue: '0',
                label: 'Vendor Code',
                order: 0,
                value: virtualVcChild.vendorCode,
                visible: true,
              },
              ...virtualVcChild.staticvalues,
            ].map((data: StaticValue, index: number): StaticValue => {
              data['order'] = index;
              return data;
            });
          }
        }
        const childDetails = {
          basicParameters: basicParameters,
          data: virtualVcChild,
          validQuota: validQuota,
        };
        virtualVcForecasts.push(childDetails as ForecastVirtualVc);
      }
    } else {
      //NO PARTS AVAILABLE
    }
    return virtualVcForecasts;
  }

  async getVirtualPnChildList(partNumber: string): Promise<VirtualPNDetail[]> {
    const partNumberList = await this.vpnsService.getPartNumbersListForSV(partNumber);
    return partNumberList;
  }

  getValidCombination(partNumber: string, vendorCode: string): Observable<ValidCombination> {
    let params = new HttpParams();
    params = params.append('PartNumber', partNumber.toUpperCase());
    params = params.append('vendorCode', vendorCode);

    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.ValidatePartNumberVendorCode}`;
    return this.http.get<ValidCombination>(url, {
      params: params,
    });
  }

  async getDefaultList(key: string): Promise<string[]> {
    const defaultFields: string[] = await firstValueFrom(this.getDefaultFormSettingFields(key));
    return defaultFields;
  }

  async getReadOnlyList(): Promise<string[]> {
    let tReadOnlyList: string[];
    tReadOnlyList = await firstValueFrom(this.getReadOnlyFieldsForCommits());
    if (tReadOnlyList) {
      tReadOnlyList = tReadOnlyList.map((element): string => {
        return element ? element.trim().toLowerCase() : element;
      });
    }

    return tReadOnlyList ? tReadOnlyList : [];
  }

  getMandatoryFieldsUrl(): Observable<string[]> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.GetMandatoryFields}`;
    return this.http.get<string[]>(url);
  }

  async getMandatoryFields(): Promise<string[]> {
    const mandatoryFields = await firstValueFrom(this.getMandatoryFieldsUrl());
    return mandatoryFields;
  }

  setFieldsVisibility(
    defaultFields: string[],
    readOnlyFields?: string[],
    mandatoryDates?: string[],
    mandatoryFields?: string[],
  ): string[] {
    let modifiedFields: string[] = [];
    if (defaultFields) {
      if (readOnlyFields) {
        modifiedFields = defaultFields.filter(
          (obj): boolean => !readOnlyFields.includes(obj.toLowerCase()),
        );
      }
    }

    if (mandatoryDates) {
      for (let i = 0; i < mandatoryDates.length; i++) {
        if (modifiedFields.indexOf(mandatoryDates[i].toLowerCase()) === -1) {
          modifiedFields.push(mandatoryDates[i].toLowerCase());
        }
      }
    }

    if (mandatoryFields) {
      mandatoryFields.forEach((mandatoryField: string, index: number): void => {
        if (modifiedFields.indexOf(mandatoryFields[index].toLowerCase()) === -1) {
          modifiedFields.push(mandatoryField);
        }
      });
    }

    return modifiedFields;
  }

  /**
   * GetCumulativeList data
   * @returns {Observable<any>}
   * @memberof SupplyVisibilityService
   */
  getCumulativeList(data: unknown): Observable<Forecast> {
    return this.http.post<Forecast>(`${this.getApiUrlSupplyVisibility('MultiProjection')}`, data);
  }

  /**
   * Get Purchase Orders
   * @param {BasicParameters} parameters
   * @returns
   * @memberof SupplyVisibilityService
   */
  getPurchaseOrders(parameters: BasicParameters): Observable<PurchaseOrders[]> {
    let params = new HttpParams();
    params = params.append('Plant', parameters?.plant as string);
    params = params.append('PartNumber', parameters?.partNumber?.toUpperCase() as string);
    if (parameters?.vendorCode) {
      params = params.append(
        'VendorCode',
        Helper.virtualVCReplace(parameters.vendorCode.toUpperCase()),
      );
    }
    return <Observable<PurchaseOrders[]>>this.http.get(
      `${this.getApiUrlPurchaseOrders('GetPurchaseOrders')}`,
      {
        params: params,
      },
    );
  }

  /**
   * Get Commit Api URL for Get commit
   * @param {BasicParameters} basicParams
   * @param {DateRangeParameters} dateRangeParams
   * @param {string} [action='GetCommits']
   * @returns
   * @memberof SupplyVisibilityService
   */
  getApiUrl(
    basicParams: BasicParameters,
    dateRangeParams: DateRangeParameters,
    action = 'GetCommits',
  ): string {
    return `${this.getApiUrlCommits(`${action}`)}?$inlinecount=allpages&$top=0&$skip=0&Plant=${
      basicParams.plant
    }&PartNumber=${basicParams.partNumber}&DeliveryDateFrom=${
      dateRangeParams.dateFrom
    }&DeliveryDateTo=${dateRangeParams.dateTo}`;
  }

  /**
   * Get Date Range
   * @param {string} [type]
   * @returns {DateRangeParameters}
   * @memberof SupplyVisibilityService
   */
  /**
   * Convert data to specified format
   * @param {DateRangeParameters} parameters
   * @param {string} type
   * @returns
   * @memberof SupplyVisibilityService
   */
  convertDate<K extends keyof DateRangeParameters>(
    parameters: DateRangeParameters,
    key: K,
  ): string {
    const value = parameters[key];

    try {
      return formatDate(value as Date | string, 'yyyy-MM-dd', 'en-US');
    } catch {
      return String(value);
    }
  }

  /**
   * Weeks in array
   * @param {ForecastDetail[]} forecastDetail
   * @returns {number[]}
   * @memberof SupplyVisibilityService
   */
  /**
   * Weeks index array for Range Navigator
   * @param {ForecastDetail[]} forecastDetail
   * @returns {number[]}
   * @memberof SupplyVisibilityService
   */
  /**
   * Get forecast range
   * @param {ForecastDetail[]} forecastDetail
   * @returns
   * @memberof SupplyVisibilityService
   */
  /**
   * Set Date Range
   * @param {DateRangeParameters} dateParameters
   * @memberof SupplyVisibilityService
   */
  setDateRange(dateParameters: DateRangeParameters): void {
    if (dateParameters && dateParameters.dateFrom && dateParameters.dateTo) {
      this.store.dispatch(new SetDateRangeParameters(dateParameters));
    } else {
      dateParameters = Helper.getCommitHistoryDateRange();
      this.store.dispatch(new SetDateRangeParameters(dateParameters));
    }
  }

  /**
   * Set Material Management Views
   */
  materialManagementViews(variant?: string): Observable<MaterialManagementViews[]> {
    let params = new HttpParams();
    if (variant) {
      params = params.append('variant', variant);
    }

    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.mmViews}`;
    return this.http.get<MaterialManagementViews[]>(url, {
      params: variant ? params : undefined,
    });
  }

  /**
   * Get Chart data from Qlik
   * @todo set to real files based on partnumber and vendor
   */
  getQlikData(
    partNumber: string,
    vendor: string,
    appId: string,
    tableId: string,
  ): Observable<unknown> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.qlikGetDataFromApp}`;
    const filter = [
      {
        field: 'Material',
        value: partNumber.toUpperCase(),
      },
      {
        field: 'Vendor',
        value: vendor,
      },
    ];
    const postData = {
      applicationId: appId,
      filter: filter,
      tableId: tableId,
    };

    return this.http.post(url, postData);
  }

  getChartSettings(): Promise<ChartSetting[]> {
    // const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.chartSettings}`;
    // return this.http.get(url);
    const appId = this.getQlikAppId() as string; //use only momentarily, then it will be set in settings

    const chartSettings = [
      {
        data: null,
        dateKey: 'edi_date_fcstaccu',
        downloadedData: null,
        name: 'FcstAccuLeadTime',
        order: 1,
        qlikAppId: appId,
        series: [
          {
            name: 'Sum of Forecast',
            nameKey: 'edi_date_fcstaccu',
            valueKey: 'fcst_rsum',
          },
          {
            name: 'cons_qty',
            nameKey: 'edi_date_fcstaccu',
            valueKey: 'cons_qty',
          },
        ],
        showDateAsWeeks: true,
        tableName: 'zjteqp',
        type: 'line',
        xAxisLabel: 'Week',
        yAxisLabel: 'Forecast',
      },
      {
        data: null,
        dateKey: 'edi_date_consweekly',
        downloadedData: null,
        name: 'ConsWeekly',
        order: 2,
        qlikAppId: appId,
        series: [
          {
            name: 'Sum of Forecast',
            nameKey: 'edi_date_consweekly',
            valueKey: 'consqtyweekly',
          },
        ],
        showDateAsWeeks: false,
        tableName: 'SFPJXy',
        type: 'bar',
        xAxisLabel: 'Week',
        yAxisLabel: 'Forecast',
      },
      {
        data: null,
        dateKey: 'datemsr',
        downloadedData: null,
        name: 'MSR',
        order: 3,
        qlikAppId: appId,
        series: [
          {
            name: 'MSR',
            nameKey: 'datemsr',
            valueKey: 'bestmsrshortage',
          },
        ],
        showDateAsWeeks: true,
        tableName: 'Ukh',
        type: 'bar',
        xAxisLabel: 'Week',
        yAxisLabel: 'Forecast',
      },
    ];
    return new Promise((resolve): void => {
      setTimeout((): void => {
        resolve(chartSettings);
      }, 100);
    });
  }

  setMarkPnReviewed(reviewFlag: string, PnVc: PnReviewed, isFlagAvailable?: boolean): void {
    //BlockGuards
    if (!reviewFlag) {
      return;
    }

    if (!isFlagAvailable) {
      PnVc.flag = reviewFlag;
      this.store.dispatch(new SetPnReviewed(PnVc));
    } else {
      this.store.dispatch(new DeletePnFlags(PnVc, reviewFlag));
    }
  }

  // * Create list of partnumbers from the widget so it is always the same
  //following method createPartNumbersList is deprecated
  createPNVendorList(
    partNumbersList: string,
    vendorCode: null | string,
    separateByTab: boolean = false,
  ): null | PNVendor[] {
    if (!partNumbersList) {
      return null;
    }

    const separatorRegex = separateByTab ? /[\|]|\t/ : /[\|]/;

    return partNumbersList
      .trim()
      .split(/\r\n|\n|\r|,/)
      .map((c): string => c.trim())
      .filter((c): boolean => c.length > 0)
      .map((c): PNVendor => {
        const pnVn = c.split(separatorRegex).filter(Boolean);

        return {
          partNumber: pnVn[0]?.trim() ?? '',
          vendorCode: pnVn[1]?.trim() ?? vendorCode,
        };
      });
  }

  /**
   *  Accepts field having values seperated by tab (/n) and returns array of values
   * @param fields
   * @returns Array
   */
  createFieldsList(fields: string): string[] {
    const fieldList = fields
      ? fields
          .trim()
          .split(/\r\n|\n|\r|,/)
          .filter((c): string => c)
          .map((c): string => (c ? c.trim() : ''))
      : [];
    return fieldList;
  }

  /**
   * Check if string is divided by custom separator
   * @param partNumbersList
   * @param separator
   */
  /**
   * getPartNumberFromPartNumberVendor string
   * @param partnumberVendor separated by vendorSeparator or |
   * @param vendorSeparator custom or undefined
   */
  getPartNumberFromPartNumberVendor(partnumberVendor: string, vendorSeparator: string): string {
    let partNumber: string = partnumberVendor;
    if (!vendorSeparator) {
      vendorSeparator = '|';
    }
    if (partnumberVendor !== '') {
      const tmpPnVendor = partnumberVendor.split(vendorSeparator);
      if (tmpPnVendor.length > 1) {
        partNumber = tmpPnVendor[0];
      }
    }
    return partNumber;
  }

  //new
  async downloadSupplyVisibilityTableByWidget(
    partnumbersList: unknown,
    live?: unknown,
    comments?: boolean,
  ): Promise<boolean> {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant) as string;
    if (!plant) {
      this.notificationService.showError(
        'Plant is not available. Cannot download Supply Visibility table.',
      );
      return false;
    }

    const dataUrl =
      `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.getProjectionsByPrecalculations}`.replace(
        '__plant__',
        plant.toLowerCase(),
      );

    await this.reDownloadSupplyVisibilityTablePrecalculations(
      dataUrl,
      partnumbersList,
      !!live,
      comments,
    );

    return true;
  }

  getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  async downloadSupplyVisibility(
    partnumbersList: BasicParameters,
    withComments?: boolean,
  ): Promise<boolean> {
    const excelDownloadInstructions: ExcelDownloadInstructions = {
      fontSize: partnumbersList.fontSize ? partnumbersList.fontSize : 10,
      materialManagementViewID: partnumbersList.mmViewID,
      partNoVendors: [],
      plant: this.store.selectSnapshot(AuthenticationSelectors.getActiveplant),
      records: partnumbersList.records,
      timeZone: this.getUserTimezone(),
      userId: this.store.selectSnapshot(AuthenticationSelectors.primarySid),
      variant: partnumbersList.variant,
    };
    //this.svUrlList = [];
    //this.svUrlList.push(this.getUrlForForecastWithParams(partnumbersList, this.dummyCommitHeaderForExcel, false));

    if (this.svUrlList.length === 0) {
      this.svUrlList.push(
        this.getUrlForForecastWithParams(partnumbersList, this.dummyCommitHeaderForExcel, false),
      );
    }

    const encodeParams = (params: { [key: string]: string }): { [key: string]: string } => {
      const paramsToEncode = ['PartNumber', 'VendorCode'];
      paramsToEncode.forEach((param: string): void => {
        if (params[param]) {
          params[param] = encodeURIComponent(params[param]);
        }
      });
      return params;
    };
    this.svUrlList = this.svUrlList.map((svUrlWithParams: SvUrlWithParams): SvUrlWithParams => {
      return {
        params: encodeParams(svUrlWithParams?.params),
        url: svUrlWithParams.url,
      };
    });
    this.svUrlList.forEach((svUrlWithParams: SvUrlWithParams): void => {
      const partNoVendor: string = `${svUrlWithParams.url}?${svUrlWithParams.params.toString()}`;
      excelDownloadInstructions?.partNoVendors?.push({ url: partNoVendor });
    });
    await this.downloadSupplyVisibilityTable(excelDownloadInstructions, withComments || false);
    return true;
  }

  async reDownloadSupplyVisibilityTablePrecalculations(
    dataUrl: string,
    partNumberList: unknown,
    live?: boolean,
    comments?: boolean,
  ): Promise<boolean> {
    const baseUrl = this.configService.getSettings('excelServiceStor');
    let res: null | PrecalculationResponse = null;

    const apiUrl = live
      ? `${baseUrl}/api/v1/SupplyVisibility/Background/Precalculations/`
      : `${baseUrl}/api/v1/SupplyVisibility/Background/Precalculations${comments ? 'WithComments' : ''}/`;

    const body = {
      partNumbersList: partNumberList,
      timeZone: this.getUserTimezone(),
      url: dataUrl,
    };

    try {
      const options = live ? { params: new HttpParams() } : undefined;

      res = await firstValueFrom(this.http.post<PrecalculationResponse>(apiUrl, body, options));
    } catch (e: unknown) {
      const err = e as HttpErrorResponse;
      if (err?.error?.errorMessage) {
        Helper.showError(
          this.notificationService,
          err.error.errorMessage,
          `Error while downloading SV. ${err.error.errorMessage}`,
        );
      } else {
        Helper.showError(this.notificationService, err, 'Error while PN list download process');
      }
      return false;
    }

    if (!res?.success?.url) {
      return false;
    }

    window.location.href = baseUrl + res.success.url;
    return true;
  }

  getQlikAppId(): unknown {
    try {
      const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant);
      const id: string = this.configService.getSettings(`svQlikAppIdByPlant.${plant}`) as string;
      if (id) {
        console.log('Using qlik app id: ' + id + ' for plant: ' + plant);
        return id;
      } else {
        throw new Error('No qlik app id found');
      }
    } catch (error) {
      console.log(error);
      return this.configService.getSettings('svQlikAppId');
    }
  }

  async downloadWaterfallByWidget(data: unknown): Promise<boolean> {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.getActiveplant) as string;
    const dataUrl = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.waterfall}`;
    await this.reDownloadWaterfallByWidget(dataUrl.replace('__plant__', plant.toLowerCase()), data);
    return true;
  }

  async reDownloadWaterfallByWidget(dataUrl: string, data: unknown): Promise<boolean> {
    const baseUrl = this.configService.getSettings('excelServiceStor');
    const params = new HttpParams();

    let res: null | PrecalculationResponse = null;

    try {
      res = await firstValueFrom(
        this.http.post<PrecalculationResponse>(
          `${baseUrl}/api/v1/Waterfall/Background/Download`,
          {
            request: data,
            url: dataUrl,
          },
          { params },
        ),
      );
    } catch (e: unknown) {
      const err = e as HttpErrorResponse;
      if (err?.error?.errorMessage) {
        Helper.showError(
          this.notificationService,
          err.error.errorMessage,
          `Error while downloading SV. ${err.error.errorMessage}`,
        );
      } else {
        Helper.showError(this.notificationService, err, 'Error while PN list download process');
      }
      return false;
    }

    if (!res?.success?.url) {
      return false;
    }

    window.location.href = baseUrl + res.success.url;
    return true;
  }

  async downloadSupplyVisibilityTable(
    data: ExcelDownloadInstructions,
    withComments?: boolean,
  ): Promise<boolean> {
    const baseUrl = this.configService.getSettings('excelServiceStor');

    const payload = {
      ...data,
      timeZone: this.getUserTimezone(),
    };

    const encodeUrlWithParams = (rawUrl: string): string => {
      const paramsToEncode = ['PartNumber', 'VendorCode'];

      const urlObj = new URL(rawUrl);
      const params = new URLSearchParams(urlObj.search);

      paramsToEncode.forEach((param): void => {
        const value = params.get(param);
        if (value) {
          params.set(param, encodeURIComponent(value));
        }
      });

      return `${urlObj.origin}${urlObj.pathname}?${params.toString()}`;
    };

    if (payload.partNoVendors?.length) {
      payload.partNoVendors = payload.partNoVendors.map(({ url }): PartNoVendorUrl => ({
        url: encodeUrlWithParams(url),
      }));
    }

    const apiUrl =
      `${baseUrl}/api/v1/SupplyVisibility/Background/Online` +
      (withComments ? 'WithComments' : '') +
      '/';

    let res: null | PrecalculationResponse;

    try {
      res = await firstValueFrom(this.http.post<PrecalculationResponse>(apiUrl, payload));
    } catch (e: unknown) {
      const err = e as HttpErrorResponse;
      if (err?.error?.errorMessage) {
        Helper.showError(
          this.notificationService,
          err.error.errorMessage,
          `Error while downloading SV table: ${err.error.errorMessage}`,
        );
      }
      return false;
    }

    if (!res?.success?.url) {
      return false;
    }

    window.location.href = baseUrl + res.success.url;
    return true;
  }

  async downloadMultiPNSupplyVisibilityTable(
    data: unknown,
    withComments?: boolean,
  ): Promise<boolean> {
    const baseUrl = this.configService.getSettings('excelServiceStor');

    const apiUrl =
      `${baseUrl}/api/v1/SupplyVisibility/Background/Online` +
      (withComments ? 'WithComments' : '') +
      '/';

    let res: null | PrecalculationResponse = null;

    try {
      res = await firstValueFrom(this.http.post<PrecalculationResponse>(apiUrl, data));
    } catch (e: unknown) {
      const err = e as HttpErrorResponse;
      if (err?.error?.errorMessage) {
        Helper.showError(
          this.notificationService,
          err.error.errorMessage,
          `Error while downloading SV table: ${err.error.errorMessage}`,
        );
      }
      return false;
    }

    if (!res?.success?.url) {
      return false;
    }

    window.location.href = baseUrl + res.success.url;
    return true;
  }

  downloadFileByUrl(url: string): Observable<Blob> {
    return this.http.get(this.rebaseToApi(url), { responseType: 'blob' });
  }

  private rebaseToApi(url: string): string {
    if (!URL.canParse(url)) return url;
    const { pathname, search } = new URL(url);
    const base = (this.configService.getSettings('excelServiceStor') as string) ?? this.apiUrl;
    return `${base.replace(/\/+$/, '')}${pathname}${search}`;
  }

  downloadCumulativeListUrl$(payload: CumulativeListDownloadPayload): Observable<string> {
    const excelatorUrl = this.configService.getSettings('excelServiceStor');
    const activePlant = (
      (this.store.selectSnapshot(AuthenticationSelectors.getActiveplant) ||
        payload.Plant ||
        payload.plant ||
        '') as string
    ).toLowerCase();
    const rawMultiProjectionUrl =
      this.apiUrl +
      environment.modulesBaseUrl.materialManagement.supplyVisibility +
      '/MultiProjection';
    const multiProjectionUrl = activePlant
      ? rawMultiProjectionUrl.replace('__plant__', activePlant)
      : rawMultiProjectionUrl;

    const requestBody = Object.assign({}, payload, {
      timeZone: this.getUserTimezone(),
      url: multiProjectionUrl,
    });

    return this.http
      .post<CumulativeListDownloadResponse>(
        excelatorUrl + '/api/v1/SupplyVisibility/Background/MultiProjection',
        requestBody,
      )
      .pipe(
        map((res): string => {
          const url = res && res.success && res.success.url;
          return url ? excelatorUrl + url : '';
        }),
        catchError((err): Observable<never> => {
          // tu len premapuj error, UI nerieš
          return throwError((): unknown => err);
        }),
      );
  }

  getUserDataRuleEndpoint(action: string): string {
    return `${this.apiUrl}${
      environment.modulesBaseUrl.materialManagement.userDataRule
    }${'/'}${action}`;
  }

  getUserVendorCode(data: ApiQueryParams): Observable<unknown> {
    let params = new HttpParams();
    if (data) {
      if (data.skip !== null && data.skip !== undefined) {
        params = params.append('$skip', data.skip);
      }
      if (data.top !== null && data.top !== undefined) {
        params = params.append('$top', data.top);
      }
      if (data.sort !== null && data.sort !== undefined) {
        params = params.append('$orderby', data.sort as string);
      }
      if (data.searchValue) {
        params = params.append('searchText', data.searchValue);
      }
    }
    return this.http.get(`${this.getUserDataRuleEndpoint('GetUserVendorCode')}`, {
      params: params,
    });
  }

  downloadVendorRights(): Observable<Blob> {
    let url = `${this.getUserDataRuleEndpoint('getuploaduservendorcodes')}`;
    return this.http.post(url, null, { responseType: 'blob' });
  }

  uploadVendorRights(file: File): Observable<RequestStatus> {
    const formData: FormData = new FormData();
    formData.append('Content', file);

    return <Observable<RequestStatus>>this.http.post(
      `${this.getUserDataRuleEndpoint('postuploaduservendorcodes')}`,
      formData,
      {
        reportProgress: true,
        responseType: 'json',
      },
    );
  }

  batchEditVendorCodesRightsRequestStatus(requestHash: string): Observable<RequestStatus> {
    return <Observable<RequestStatus>>(
      this.http.get(`${this.getUserDataRuleEndpoint('-')}/RequestStatus/${requestHash}`)
    );
  }

  insertUserVendorCodeRights(data: unknown): Observable<unknown> {
    return this.http.post(`${this.getUserDataRuleEndpoint('InsertUserVendorCode')}`, data);
  }

  updateUserVendorCodeRights(data: unknown): Observable<unknown> {
    console.log('updateUserVendorCodeRights', data);
    return this.http.post(`${this.getUserDataRuleEndpoint('UpdateUserVendorCode')}`, data);
  }

  deleteUserVendorCodeRights(id: string): Observable<unknown> {
    return this.http.delete(`${this.getUserDataRuleEndpoint('DeleteUserVendorCode')}/${id}`);
  }

  /**
   * Shared Widget Settings
   */
  private getSharedWidgetEndpoint(): string {
    return `${this.userSettingsUrl}${environment.modulesBaseUrl.materialManagement.sharedWidgets}`;
  }

  insertSharedWidget(widgetData: SharedWidget): Observable<SharedWidget> {
    return <Observable<SharedWidget>>this.http.post(this.getSharedWidgetEndpoint(), widgetData);
  }

  getSharedWidgetList(): Observable<SharedWidgets[]> {
    return <Observable<SharedWidgets[]>>this.http.get(`${this.getSharedWidgetEndpoint()}`);
  }

  getSharedWidgetById(widgetId: unknown): Observable<SharedWidget> {
    return <Observable<SharedWidget>>this.http.get(`${this.getSharedWidgetEndpoint()}/${widgetId}`);
  }

  updateSharedWidget(widgetData: SharedWidget): Observable<unknown> {
    return this.http.put(`${this.getSharedWidgetEndpoint()}/${widgetData.id}`, widgetData);
  }

  deleteSharedWidget(widgetId: string): Observable<unknown> {
    return this.http.delete(`${this.getSharedWidgetEndpoint()}/${widgetId}`);
  }

  /**
   * Shared Widget Settings
   */

  private getDashboardEndpoint(): string {
    return `${this.userSettingsUrl}${environment.modulesBaseUrl.materialManagement.dashboards}`;
  }

  private getTemplateSettingsEndpoint(): string {
    return `${this.userSettingsUrl}${environment.modulesBaseUrl.materialManagement.templateSettings}`;
  }

  insertDashboard(dashboard: DashboardSetting): Observable<DashboardSetting> {
    return <Observable<DashboardSetting>>(
      this.http.post(`${this.getDashboardEndpoint()}`, dashboard)
    );
  }

  updateDashboard(dashboard: DashboardSetting): Observable<unknown> {
    return this.http.put(`${this.getDashboardEndpoint()}/${dashboard.id}`, dashboard);
  }

  getDashboardList(): Observable<DashboardSetting[]> {
    return <Observable<DashboardSetting[]>>this.http.get(`${this.getDashboardEndpoint()}`);
  }

  getDashboardDetail(id: string): Observable<DashboardSetting> {
    return <Observable<DashboardSetting>>this.http.get(`${this.getDashboardEndpoint()}/${id}`);
  }

  deleteDashboard(id: string): Observable<unknown> {
    return this.http.delete(`${this.getDashboardEndpoint()}/${id}`);
  }

  getTemplateSettings(data: TemplatePayload): Observable<unknown> {
    let params = new HttpParams();
    params = params.append('plant', data.plant as string);
    params = params.append('type', data.type as string);
    params = params.append('key', data.key as string);

    return this.http.get(`${this.getTemplateSettingsEndpoint()}`, {
      params: params,
    });
  }

  getTemplateSettingsByID(id: string): Observable<unknown> {
    return this.http.get(`${this.getTemplateSettingsEndpoint()}/${id}`);
  }

  updateTemplateSettings(payload: TemplatePayload): Observable<unknown> {
    return this.http.put(`${this.getTemplateSettingsEndpoint()}/${payload.id}`, payload);
  }

  createTemplateSettings(data: unknown): Observable<unknown> {
    return this.http.post(`${this.getTemplateSettingsEndpoint()}`, data);
  }

  deleteTemplateSettings(id: string): Observable<unknown> {
    return this.http.delete(`${this.getTemplateSettingsEndpoint()}/${id}`);
  }

  private forecastDateCache$?: Observable<ForecastDate>;

  getForecastDate(): Observable<ForecastDate> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.forecastDate}`;
    if (!this.forecastDateCache$) {
      this.forecastDateCache$ = this.http.get<ForecastDate>(url).pipe(shareReplay(1));
    }
    return this.forecastDateCache$;
  }

  getReadOnlyFieldsForCommits(): Observable<string[]> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.commitsReadOnlyFields}`;
    return <Observable<string[]>>this.http.get(url);
  }

  getFilterAdditionsForCommits(): Observable<unknown[]> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.commitsFilterAdditions}`;
    return <Observable<unknown[]>>this.http.get(url);
  }

  /**
   * Notifications
   */
  private getNotificationsEndpoint(): string {
    return `${this.userSettingsUrl}${environment.modulesBaseUrl.materialManagement.notifications}`;
  }

  getNotificationsList(): Observable<ApiResponse<SVNotification[]>> {
    return <Observable<ApiResponse<SVNotification[]>>>(
      this.http.get(`${this.getNotificationsEndpoint()}`)
    );
  }

  getNotificationDetail(id: string): Observable<SVNotification> {
    return <Observable<SVNotification>>this.http.get(`${this.getNotificationsEndpoint()}/${id}`);
  }

  getNotificationsUnreadCount(): Observable<{ count: number }> {
    return <Observable<{ count: number }>>(
      this.http.get(`${this.getNotificationsEndpoint()}/unreadcount`)
    );
  }

  setNotificationAsRead(id: string): Observable<unknown> {
    return this.http.put(`${this.getNotificationsEndpoint()}/${id}/setasread`, null);
  }

  setAllNotificationsAsRead(): Observable<boolean> {
    return <Observable<boolean>>(
      this.http.post(`${this.getNotificationsEndpoint()}/setallasread`, null)
    );
  }

  getFilterList(
    field: string,
    filters: AdditionalFilter[] | null,
    ignoreSpace = false,
    useSemicolon = false,
  ): boolean | null | string | string[] | unknown {
    if (!filters?.length) {
      return null;
    }

    const filter = filters.find((f): boolean => f.field === field);
    if (!filter) {
      return null;
    }

    // Special cases
    if (field === 'filterLeadtimeQuery') {
      return filter.value ? `${filter.operator ?? ''}${filter.value}` : null;
    }

    if (field === 'filterVirtualVC') {
      return filter.value; // boolean | null
    }

    if (field === 'filterVirtualVCIncludeChild') {
      return filter.value; // boolean | null
    }

    // Normal string splitting logic
    if (!filter.value || typeof filter.value !== 'string') {
      return [];
    }

    const delimiter = useSemicolon ? ';' : ',';
    const regex = ignoreSpace ? new RegExp(`[${delimiter}]`) : new RegExp(`\\s+|[${delimiter}]`);

    return filter.value
      ?.split(regex)
      .map((v): string => v.trim())
      .filter(Boolean);
  }

  getQapUrl(): string {
    let url: string = '';
    let qap = this.configService.getSettings('qap') as QapConfig;
    if (qap) {
      let params = new HttpParams();
      params = params.append('username', `*${qap.domain}${this.authService.getUserName()}*`);
      params = params.append('appid', qap.appId as string);
      params = params.append('sheet', qap.sheet as string);
      params = params.append('opt', 'currsel');
      params = params.append('select', 'clearall');
      url = `${qap.url}?${params.toString()}`;
    }
    return url;
  }

  syncPLO(): Observable<unknown> {
    const url = `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.syncPlo}`;
    //NOTE - Need to set responseType as response is of string type not JSON
    return this.http.get(url, { responseType: 'text' });
  }

  qapLogin(): Observable<unknown> {
    const url = 'https://cz03qap11.cz.foxconn.com/jwt/qrs/about?xrfkey=0000000000000000';
    const headers = new HttpHeaders().append('X-Qlik-xrfkey', '0000000000000000');
    return this.http.get(url, { headers: headers, responseType: 'text', withCredentials: true });
  }

  /**
   * Which fields the create form shows, as the user arranged them.
   *
   * The same list in the same shape as a grid's columns, stored the same way —
   * only what draws it differs.
   * @returns {Observable<ColumnsState>} The fields, and whether they are still the defaults.
   */
  getCommitFormColumns = (): Observable<ColumnsState> =>
    // Straight to the loader rather than through `ColumnsService`: that service
    // reads and writes settings through this one, so injecting it here would
    // close a cycle. This service already owns both ends of it.
    loadDefaultColumns({
      buildDefaults: (formFields: string[]): Column[] =>
        new CommonCommitFields(EnumCommitFieldStructure.CommitForm, formFields)
          .fields as unknown as Column[],
      columnListKey: COMMIT_FORM_FIELD_LIST,
      formFields$: this.getDefaultFormSettingFields(COMMIT_FORM_FIELDS_KEY),
      persistDefaults: (key: string, columns: Column[]): Observable<void> =>
        this.addUserSettings({ data: JSON.stringify(toStoredColumns(columns)), key }, false).pipe(
          map((): void => undefined),
        ),
      userSettings: (key: string): Observable<UserSettings> => this.getUserSettings(key),
    });
}
